import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";

type TrailerDemoProps = {
  poster: string;
  title: string;
  duration: string;
  workTitle: string;
};

const toSeconds = (label: string) => {
  const parts = label.split(":").map((part) => Number(part));
  if (parts.length === 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) return parts[0] * 60 + parts[1];
  return 60;
};

const formatTime = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export default function TrailerDemo({ poster, title, duration, workTitle }: TrailerDemoProps) {
  const total = toSeconds(duration);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev + 1 >= total) {
          setPlaying(false);
          return total;
        }
        return prev + 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [playing, total]);

  const togglePlay = () => {
    if (!playing && progress >= total) setProgress(0);
    setPlaying((prev) => !prev);
  };

  const seek = (event: MouseEvent<HTMLDivElement>) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    setProgress(Math.round(ratio * total));
  };

  const percent = total === 0 ? 0 : (progress / total) * 100;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-base md:text-lg font-semibold text-foreground-950">作品预告</h2>
        <span className="text-[11px] px-2 py-1 rounded bg-secondary-100 text-secondary-900 whitespace-nowrap">
          前端演示 · 无真实视频流
        </span>
      </div>

      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-accent-950">
        <img
          src={poster}
          alt={`${workTitle} 预告片封面（演示）`}
          title={`${workTitle} 视频预告演示`}
          className="w-full h-full object-cover object-top opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-accent-950/90 via-accent-950/30 to-accent-950/10"></div>

        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4 text-center">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? "暂停预告" : "播放预告"}
            className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-background-50/95 text-primary-600 hover:bg-background-50 transition-colors cursor-pointer"
          >
            <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-2xl md:text-3xl`}></i>
          </button>
          <div>
            <p className="text-sm md:text-base font-medium text-background-50">{title}</p>
            <p className="text-[11px] md:text-xs text-background-200/80 mt-1">{playing ? "演示播放中…" : "点击播放按钮开始演示"}</p>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 p-3 md:p-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? "暂停" : "播放"}
              className="w-8 h-8 flex items-center justify-center rounded-full text-background-50 hover:bg-background-50/20 transition-colors cursor-pointer shrink-0"
            >
              <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-base`}></i>
            </button>
            <div
              ref={barRef}
              onClick={seek}
              role="presentation"
              className="flex-1 h-1.5 rounded-full bg-background-50/25 cursor-pointer relative"
            >
              <div className="absolute inset-y-0 left-0 rounded-full bg-primary-400" style={{ width: `${percent}%` }}></div>
              <div
                className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-background-50"
                style={{ left: `calc(${percent}% - 5px)` }}
              ></div>
            </div>
            <span className="text-[11px] text-background-100 font-heading shrink-0">
              {formatTime(progress)} / {duration}
            </span>
          </div>
        </div>
      </div>

      <p className="mt-2.5 text-[11px] text-foreground-400 leading-relaxed">
        说明：此模块仅用于演示播放开始、暂停与进度切换的交互效果，不包含真实流媒体、下载或网络加载。
      </p>
    </div>
  );
}